import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Grid, Hidden, Tooltip, useMediaQuery } from '@material-ui/core';

// COMPONENTS
import PoweredBy from '@/components/PoweredBy';

// HELP
import ChannelList from './help/ChannelList/ChannelList';
import ServiceList from './help/ServiceList/ServiceList';

// STYLES
import { FooterAppBar, StyledToolbar, StyledButton } from './styles';

const FooterWizard = props => {
  const {
    type,
    onPrevPress,
    onNextPress,
    onSubmit,
    showPrev,
    showNext,
    showSubmitBtn,
    prevBtnProps,
    nextBtnProps,
    hiddenProps,
    submitBtnProps,
    withShadow,
    channels,
    onChannelRemove,
    services,
    onServiceRemove,
    onUnselectAll,
    partner,
    location,
    pageName,
    isIframe,
    pageContent,
  } = props;
  const [t] = useTranslation();
  const isMobile = useMediaQuery(({ breakpoints }) => breakpoints.down('xs'));
  const isResults = type === 'results' || type === 'csr-results';
  const isOnlyButtons = type === 'only-buttons';
  const isOnlyChannels = type === 'only-channels';
  const isChannels = type === 'channels';
  const isServices = type === 'services';
  const isFmb = type === 'fmb';
  const isSuperbundleResults = type === 'superbundle-results';
  const isChannelsStep1IframeMobile =
    pageName === 'channels-step1' && isChannels && isMobile && isIframe;
  const isFmbChannelsMobile =
    (isChannels || isServices || isFmb || isSuperbundleResults) && isMobile;
  const showIframeFooter =
    isIframe && pageName === 'channels-step1' && partner.settings?.show_iframe_footer;

  const channelsContent = channels.length ? (
    <ChannelList
      type={type}
      channels={channels}
      onChannelRemove={onChannelRemove}
      onUnselectAll={onUnselectAll}
      analyticsLocation={location}
      channelsCountForAlert={pageContent && +pageContent['channels-count-alert']}
    />
  ) : null;

  const servicesContent = services.length ? (
    <ServiceList
      type={type}
      services={services}
      onServiceRemove={onServiceRemove}
      onUnselectAll={onUnselectAll}
      analyticsLocation={location}
    />
  ) : null;

  const poweredBy = partner.home_link ? (
    <Hidden xsDown>
      <PoweredBy type="dark" />
    </Hidden>
  ) : null;

  let submitButton = null;

  if (showSubmitBtn) {
    const button = (
      <Grid item xs={isFmbChannelsMobile}>
        <StyledButton
          {...submitBtnProps}
          type="button"
          onClick={onSubmit}
          color="primary"
          size="medium"
          className="next-button"
        >
          {submitBtnProps.btntext || t('buttons.seeResults')}
        </StyledButton>
      </Grid>
    );

    submitButton = submitBtnProps.tooltip ? (
      <Tooltip title={submitBtnProps.tooltip}>{button}</Tooltip>
    ) : (
      button
    );
  }

  const getContent = contentType => {
    switch (contentType) {
      case 'results':
      case 'csr-results':
      case 'only-channels':
        return (
          channelsContent && (
            <Grid item xs={12} className="left-side">
              {channelsContent}
            </Grid>
          )
        );
      case 'superbundle-results':
        return (
          channelsContent && (
            <Grid
              item
              xs={12}
              sm={6}
              md={8}
              lg={9}
              className={clsx('left-side', showIframeFooter && 'left-side--fullwidth')}
            >
              {channelsContent}
            </Grid>
          )
        );
      case 'channels':
        return (
          channelsContent && (
            <Grid
              item
              xs={12}
              {...(showIframeFooter
                ? {}
                : {
                    sm: 6,
                    md: 8,
                    lg: 9,
                  })}
              className={clsx('left-side', showIframeFooter && 'left-side--fullwidth')}
            >
              {channelsContent}
            </Grid>
          )
        );
      case 'services':
        return (
          servicesContent && (
            <Grid
              item
              xs={12}
              {...(showIframeFooter
                ? {}
                : {
                    sm: 6,
                    md: 8,
                    lg: 9,
                  })}
              className={clsx('left-side', showIframeFooter && 'left-side--fullwidth')}
            >
              {servicesContent}
            </Grid>
          )
        );
      case 'only-buttons':
        return null;
      default:
        return (
          poweredBy && (
            <Grid item xs={12} sm={6} md={8} lg={9} className="left-side">
              {poweredBy}
            </Grid>
          )
        );
    }
  };

  return (
    <FooterAppBar
      className={clsx(type, pageName, {
        'app-bar--shadow': withShadow,
      })}
      component="footer"
    >
      <StyledToolbar type={type}>
        <Grid
          container
          spacing={isMobile ? 0 : 2}
          justifyContent="space-between"
          alignItems="center"
        >
          {getContent(type)}

          {!isResults && !showIframeFooter && !isOnlyChannels && (
            <Hidden smDown {...hiddenProps}>
              <Grid
                item
                xs={12}
                sm={isOnlyButtons ? 12 : 6}
                md={isOnlyButtons ? 12 : 4}
                lg={isOnlyButtons ? 12 : 3}
                className="right-side"
              >
                <Grid
                  container
                  spacing={1}
                  justifyContent={isOnlyButtons ? 'center' : 'flex-end'}
                  alignItems="center"
                  wrap="nowrap"
                >
                  {showPrev && (
                    <Grid
                      item
                      xs={isChannelsStep1IframeMobile}
                      className="previous-button__grid-item"
                    >
                      <StyledButton
                        {...prevBtnProps}
                        size="medium"
                        onClick={onPrevPress}
                        className="previous-button"
                      >
                        {t('buttons.previous')}
                      </StyledButton>
                    </Grid>
                  )}
                  {showNext && (
                    <Grid item xs={isFmbChannelsMobile}>
                      <StyledButton
                        {...nextBtnProps}
                        type="button"
                        color="primary"
                        size="medium"
                        onClick={onNextPress}
                        className="next-button"
                      >
                        {type === 'superbundle-results' ? t('buttons.order') : t('buttons.next')}
                      </StyledButton>
                    </Grid>
                  )}
                  {submitButton}
                </Grid>
              </Grid>
            </Hidden>
          )}
        </Grid>
      </StyledToolbar>
    </FooterAppBar>
  );
};

FooterWizard.defaultProps = {
  type: 'default', // default | results | results | csr-results | channels | only-buttons | fmb | services | superbundle-results
  onPrevPress: () => {},
  onNextPress: () => {},
  onSubmit: () => {},
  showPrev: true,
  showNext: true,
  showSubmitBtn: false,
  prevBtnProps: {},
  nextBtnProps: {},
  hiddenProps: {},
  submitBtnProps: {},
  withShadow: false,
  channels: [],
  onChannelRemove: () => {},
  services: [],
  onServiceRemove: () => {},
  onUnselectAll: () => {},
  location: '',
};

FooterWizard.propTypes = {
  type: PropTypes.string,
  onPrevPress: PropTypes.func,
  onNextPress: PropTypes.func,
  onSubmit: PropTypes.func,
  showPrev: PropTypes.bool,
  showNext: PropTypes.bool,
  showSubmitBtn: PropTypes.bool,
  prevBtnProps: PropTypes.object,
  nextBtnProps: PropTypes.object,
  hiddenProps: PropTypes.object,
  submitBtnProps: PropTypes.object,
  withShadow: PropTypes.bool,
  channels: PropTypes.array,
  onChannelRemove: PropTypes.func,
  services: PropTypes.array,
  onServiceRemove: PropTypes.func,
  onUnselectAll: PropTypes.func,
  partner: PropTypes.object.isRequired,
  pageName: PropTypes.string,
  location: PropTypes.string,
  isIframe: PropTypes.bool,
  pageContent: PropTypes.object,
};

export default React.memo(FooterWizard);
